/* Built In Imports */
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Fragment, useEffect, useState } from 'react';

/* External Imports */
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import parser from 'html-react-parser';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import MediaEmbed from '@components/Embeds/MediaEmbed';
import ProgressiveImage from '@components/UI/ProgressiveImage';
import SubscribeContent from '@components/UI/Subscribe/SubscribeContent';
import { ShortCodeProcess } from '@components/Utility/ArticleParser/ShortCode';
import SocialIcon from '@components/Utility/SocialIcon';
import config from '@config';
import { LangStyle } from 'constants/index';

/* Services */
/* Styles */
import classes from '@styles/components/ArticleComp.module.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Component for showing details of the articles.
 * @component Renders the About Container component.
 * @param {string} type - Type of page Eg: Article, Audio, Video.
 * @param {Object} articleData - Content data fron api.
 * @param {string} language - Current User Language.
 * @param {Boolean} isLikeBookmarkVisible = true or false - to show and hide the like and bookmarks.
 * @param {Function} updateCards - Function to be called when we click on Like or Bookmark button.
 * @param {string} pageType - Type of page. Eg: Sadhguru, Wisdom.
 * @return {ReactElement} About Container component.
 */

// To get the position of eg: 5th (index) para tag (subString) in the given (string), we use this function.
function getPosition(string, subString, index) {
  return string?.split(subString, index).join(subString).length;
}

const Article = ({
  type,
  articleData,
  language,
  isLikeBookmarkVisible,
  updateCards,
  pageType,
}) => {
  const router = useRouter();
  const { client } = router.query;

  const isSadhguruPage = pageType === 'sadhguru';
  const title = isSadhguruPage
    ? articleData?.title
    : articleData?.title?.[0]?.value;

  const summary = isSadhguruPage
    ? articleData?.summary
    : articleData?.summary?.[0]?.value;

  const mediaUrl = isSadhguruPage
    ? articleData?.mediaUrl?.[0]
    : articleData?.mediaUrl?.[0]?.value;

  const body = isSadhguruPage
    ? articleData?.body
    : articleData?.body?.[0]?.value;

  // Subscribe box is not visible for some of the articles and blogs.
  const isSubscribeBoxVisible = !(
    articleData?.pageConfig?.[0]?.value?.subscription_form === 'disable'
  );

  const [isShowForSadhguruApp, setIsShowForSadhguruApp] = useState(true);

  useEffect(() => {
    if (
      router.asPath.includes('client=sadhguru-app') ||
      client === 'sadhguru-app'
    ) {
      setIsShowForSadhguruApp(false);
    } else {
      setIsShowForSadhguruApp(true);
    }
  }, [router.asPath, client]);

  const NextArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top="50%"
        mt="-22px"
        zIndex="10"
        cursor="pointer"
        backgroundColor="#464038"
        border="0"
        borderRadius="100%"
        width="60px"
        height="60px"
        box-shadow="0 0 10px rgba(0, 0, 0, 0.5)"
        backgroundImage={`url(${config.cdnPath}/_next/static/media/right-arrow-big.ad9e77080e4eb64146c45f4f464e1161.svg)`}
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundSize="40px 40px"
        color="transparent"
        float="left"
        right="0"
        mr="-11px"
        onClick={onClick}
      ></Box>
    );
  };
  const PrevArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top="50%"
        mt="-22px"
        zIndex="10"
        cursor="pointer"
        backgroundColor="#464038"
        border="0"
        borderRadius="100%"
        width="60px"
        height="60px"
        box-shadow="0 0 10px rgba(0, 0, 0, 0.5)"
        backgroundImage={`url(${config.cdnPath}/_next/static/media/left-arrow-big.508d86f7f4f9a7cf617f7668ce4cf5bf.svg)`}
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundSize="40px 40px"
        color="transparent"
        left="-10px !important"
        onClick={onClick}
      ></Box>
    );
  };

  // SLider settings config
  const settings = {
    autoplay: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const articleBody = (body && ShortCodeProcess(body.toString())) || '';
  // Below Logic : If the article is long enough, we show subscribe box in middle of article, otherwise at the end.
  const paraCount = (articleBody.match(/<\/p>/g) || []).length;
  // We consider article long enough if it contains more than 8 para tags.
  const isSubscribeDynamic =
    paraCount >= 8 && body.toString().indexOf('add_subscribe_form') < 0;
  // If article is long enough, we get the position where we split the article into 2 halves so that we can insert subscribe box in between.
  const position = getPosition(articleBody, '</p>', paraCount / 3) + 4;

  const images = articleData?.images || articleData?.imageGallery?.[0]?.value;

  const HeroImageBox = ({ heroImage }) => {
    if (!heroImage) return null;

    return (
      <Box
        boxShadow="0 0 65px 7px rgb(124, 124, 124, 0.75)"
        display="inline-block"
        pos="relative"
        background="#e1e1e1"
      >
        <ProgressiveImage
          src={heroImage?.url}
          title={heroImage?.title}
          alt={heroImage?.alt}
          minHeight={{
            base: '220px',
            sm: '255px',
            md: '442.8px',
            lg: '387.48px',
            xl: '496.8px',
          }}
          display="block"
        />
      </Box>
    );
  };
  const ImageBox = ({ img, title }) => {
    const imageTitle = title !== undefined ? title : title?.[0]?.value;
    return (
      <Box pos="relative" width="100%">
        <LazyLoadImageComponent
          alt={imageTitle}
          title={imageTitle}
          src={img?.url}
          minHeight={{
            base: '220px',
            sm: '255px',
            md: '442.8px',
            lg: '387.48px',
            xl: '496.8px',
          }}
          width="100%"
        />
      </Box>
    );
  };

  return (
    <Fragment>
      <Box
        as="section"
        float="left"
        width="100%"
        textAlign="left"
        className={
          language === 'ar' ? 'articlebody-arabic' : `article-body-${language}`
        }
      >
        {mediaUrl?.heroImage?.title}
        {/* // this is visible for desktop and larger devices */}
        {isShowForSadhguruApp && (
          <SocialIcon
            url={articleData?.url}
            isVisible={true}
            isLikeBookmarkVisible={isLikeBookmarkVisible}
            isLiked={articleData?.isLiked}
            isBookmarked={articleData?.isBookmarked}
            updateCards={updateCards}
          />
        )}
        <Box width="100%" textAlign="center" pb="50px">
          <Box
            backgroundColor="#f5f5f5"
            minHeight={{ base: 'auto', lg: '300px' }}
          >
            <Heading
              as="h1"
              display="inline-block"
              width="100%"
              maxWidth="850px"
              textAlign="center"
              fontFamily={
                LangStyle[language]?.headingFont
                  ? LangStyle[language].headingFont
                  : 'FedraSerifAStdBook'
              }
              fontSize={{ base: '27px', md: '45px', lg: '45px' }}
              color="#28231e"
              margin="0"
              p={{
                base: '19px 16px',
                md: '27px 56px 16px',
                lg: '55px 0px 24px',
              }}
              fontWeight={
                LangStyle[language]?.titleFontWeight
                  ? LangStyle[language].titleFontWeight
                  : '600'
              }
            >
              {title}
            </Heading>
            <Text
              display="inline-block"
              width={{ base: '100%', lg: '60%' }}
              className={
                language === 'he' ? 'text-dir' : `none`
              }
              textAlign="center"
              fontFamily={
                LangStyle[language]?.summaryFont
                  ? LangStyle[language].summaryFont
                  : 'FedraSansStd-book-i, sans-serif'
              }
              fontStyle={language == 'mr' ? 'italic' : 'unset'}
              fontSize={{
                base: '16px',
                md: '20px',
                lg: language === 'en' ? '20px' : '22px',
              }}
              color="#8a8173"
              lineHeight={{ base: '1.5', sm: '1.5', md: '1.7', lg: '1.7' }}
              maxWidth="760px"
              m="0"
              p={{
                base: '0px 16px 31px',
                md: '0px 86px 29px',
                lg: '0 0 48px 0',
              }}
            >
              {summary}
            </Text>
          </Box>
          <Box>
            <Box
              clipPath="polygon(0 0, 100% 0, 100% 80%, 0% 100%)"
              backgroundColor="#f5f5f5"
              minHeight={{
                base: '20px',
                lg: '120px',
                xl: '240px',
              }}
              margin="-2px auto 0"
            ></Box>
            <Box
              width={{ base: '100%', lg: '66%' }}
              padding={{ base: '0', sm: '0 15px' }}
              m={{
                base: '-20px auto 0',
                sm: '-20px auto 0',
                lg: '-110px auto 0',
                xl: '-240px auto 0',
              }}
            >
              {images?.length > 0 ? (
                images.length >= 2 ? (
                  <Slider className={classes['campaign-list']} {...settings}>
                    {images.map((img, i) => (
                      <ImageBox key={i} img={img} title={articleData?.title} />
                    ))}
                  </Slider>
                ) : (
                  images.map((img, i) => (
                    <ImageBox key={i} img={img} title={articleData?.title} />
                  ))
                )
              ) : (
                <HeroImageBox heroImage={articleData?.heroImage?.[0]?.value} />
              )}
            </Box>
          </Box>
          <Box
            float="left"
            width="100%"
            textAlign="center"
            color="#aea493"
            fontFamily="FedraSansStd-book"
            fontSize="14px"
            marginTop="50px"
          >
            <Box as="span" p="0.5vw 1vw">
              Article
            </Box>
            <Box as="span" p="0.5vw 1vw" borderLeft="1px solid #dadada">
              {new Date(articleData.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </Box>
          </Box>
        </Box>

        {/* // this is visible for mobile and smaller devies */}
        {isShowForSadhguruApp && (
          <SocialIcon
            url={articleData?.url}
            isLikeBookmarkVisible={isLikeBookmarkVisible}
            id={articleData?.uuid}
            isLiked={articleData?.isLiked}
            isBookmarked={articleData?.isBookmarked}
            updateCards={updateCards}
          />
        )}

        <Flex
          direction="column"
          pos="relative"
          width="100%"
          alignItems="center"
          m={{ base: '0' }}
        >
          {isSadhguruPage && (
            <Box maxW="560px" w="full" mt="30px">
              <MediaEmbed sectionContent={mediaUrl} margin="0" />
            </Box>
          )}
          <Box
            fontFamily={
              LangStyle[language]?.articleFontFamily
                ? LangStyle[language]?.articleFontFamily
                : 'FedraSansStd-book'
            }
            fontSize={{
              base: '16px',
              md: '18px',
              lg: '20px',
            }}
            p={{
              base: '0px 15px',
              md: '0',
            }}
            color="#28231e"
            lineHeight="1.7"
            width={{ base: '100%', md: '90%', lg: '58%' }}
            className="articlebody"
            textAlign={
              LangStyle[language]?.alignRTL
                ? LangStyle[language]?.alignRTL
                : 'left'
            }
          >
            {/* If the article is long (greater than 8 <p> tags), we show subscribe box in middle of the article. */}
            <Box>
              {/* We need to enclose the parser content in Box as above otherwise its css interferes with the below parser content css */}
              {isSubscribeDynamic && parser(articleBody.substring(0, position))}
            </Box>
            {isSubscribeBoxVisible &&
              isSubscribeDynamic &&
              (language === 'en' || language === 'hi' || language === 'ta') && (
                <SubscribeContent
                  language={language}
                  contentSection="blog"
                  contentType={type}
                />
              )}
            <Box>
              {/* We need to enclose the parser content in Box as above otherwise its css interferes with the below parser content css */}
              {isSubscribeDynamic && parser(articleBody.substring(position))}
            </Box>

            {/* If the article is short, we show subscribe box at the end of the article. */}
            {!isSubscribeDynamic && parser(articleBody)}
            {isSubscribeBoxVisible &&
              !isSubscribeDynamic &&
              (language === 'en' || language === 'hi' || language === 'ta') && (
                <SubscribeContent
                  language={language}
                  contentSection="blog"
                  contentType={type}
                />
              )}
            {/* 
                Load the twitter script to show the twitter widget in the article. 
                Need to keep a watch if script tags are used other than for twitter widget 
            */}
            {(body?.includes('<script') || body?.includes('twitter.com')) && (
              <Script
                async
                src="https://platform.twitter.com/widgets.js"
                charset="utf-8"
              ></Script>
            )}
          </Box>
        </Flex>
        {/* We need to change article text direction for Arabic language */}
        <style global jsx>{`
          .articlebody-arabic {
            direction: rtl;
          }
        `}</style>
      </Box>
    </Fragment>
  );
};

export default Article;
